import { extractErrorMessage } from "./extractErrorMessage";

export class FetchJsonError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "FetchJsonError";
    this.status = status;
  }
}

/**
 * Fetches JSON from a URL with standard error handling.
 *
 * - Merges caller headers with JSON defaults (Accept, Content-Type)
 * - On non-ok responses, attempts to parse a JSON error body and extract `.error`
 * - Falls back to `response.statusText` when the body is unparseable
 * - Returns `undefined` for 204 No Content responses
 * - Wraps network-level failures (e.g. DNS, timeout) in a descriptive error
 */
export async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  let response: Response;

  try {
    response = await fetch(url, {
      ...options,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...options?.headers
      }
    });
  } catch (error) {
    throw new FetchJsonError(extractErrorMessage(error, "Network request failed"), 0);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    let message = response.statusText || String(response.status);

    try {
      const body = await response.json();
      if (typeof body.error === "string") {
        message = body.error;
      } else if (typeof body.message === "string") {
        message = body.message;
      }
    } catch {
      // Body wasn't JSON -- keep statusText or String(status) for HTTP/2 empty statusText
    }

    throw new FetchJsonError(message, response.status);
  }

  return response.json() as Promise<T>;
}
